import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './single-flight';
import { inviteEach } from '../screens/company/invite-list';
import { tracked } from '../analytics/mutation-events';
import type { QueryKey } from '@tanstack/react-query';
import { useDataSource, type DataSource } from './data-source';
import type { Company, Package, Person, PersonJobs, Role } from './types';

export const companyKeys = {
  company: (tenant: string): QueryKey => ['company', tenant],
  people: (tenant: string): QueryKey => ['company', tenant, 'people'],
  billing: (tenant: string): QueryKey => ['company', tenant, 'billing'],
};

export function billingQuery(ds: DataSource, tenant: string) {
  return queryOptions({
    queryKey: companyKeys.billing(tenant),
    queryFn: () => ds.company.billing(tenant),
    staleTime: 30_000,
  });
}

export function companyQuery(ds: DataSource, tenant: string) {
  return queryOptions({
    queryKey: companyKeys.company(tenant),
    queryFn: () => ds.company.get(tenant),
    staleTime: 30_000,
  });
}

/** The billing view; `enabled: false` for a person the API would refuse (a reviewer, a scanner). */
export const useBilling = (tenant: string, enabled = true) =>
  useQuery({ ...billingQuery(useDataSource(), tenant), enabled });

/** The company's recent changes; refreshed whenever a people or company change lands. */
export function useChanges(tenant: string, limit: number) {
  const ds = useDataSource();
  return useQuery({
    queryKey: [...companyKeys.company(tenant), 'changes', limit],
    queryFn: () => ds.company.changes(tenant, limit),
    staleTime: 10_000,
  });
}
export const useCompany = (tenant: string) => useQuery(companyQuery(useDataSource(), tenant));

/**
 * The people of the company. The API answers its admins alone (`readsPeople`), so a screen a
 * reviewer can open passes `enabled: false` and the list is never asked for.
 */
export function usePeople(tenant: string, enabled = true) {
  const ds = useDataSource();
  return useQuery({
    queryKey: companyKeys.people(tenant),
    queryFn: () => ds.company.people(tenant),
    enabled,
  });
}

/** Leaving the company yourself: anybody's own to do, without the people list. */
export function useLeaveCompany(tenant: string, userId: string) {
  const ds = useDataSource();
  return useMutation({
    mutationFn: () => ds.company.remove(tenant, { userId }),
    onSuccess: tracked('people.removed'),
  });
}

/** Package and security changes put the answered company straight into the cache. */
export function useCompanyChanges(tenant: string) {
  const ds = useDataSource();
  const qc = useQueryClient();
  const set = (company: Company) => {
    qc.setQueryData(companyKeys.company(tenant), company);
    void qc.invalidateQueries({ queryKey: [...companyKeys.company(tenant), 'changes'] });
  };
  const setPackage = useMutation({
    mutationFn: (pkg: Package) => ds.company.setPackage(tenant, pkg),
    onSuccess: tracked<Company, Package>(
      'billing.plan_switched',
      (_c, pkg) => ({ plan: pkg }),
      (company) => {
        set(company);
        // A new package opens a new period with its credits; the billing read is stale.
        void qc.invalidateQueries({ queryKey: companyKeys.billing(tenant) });
      },
    ),
  });
  const setSecurity = useMutation({
    mutationFn: (requireMfa: boolean) => ds.company.setSecurity(tenant, requireMfa),
    onSuccess: tracked<Company, boolean>('security.two_step_required', (_c, on) => ({ on }), set),
  });
  const close = useMutation({
    mutationFn: (name: string) => ds.company.close(tenant, name),
    onSuccess: tracked('company.closed', undefined, () => qc.clear()),
  });
  const update = useMutation({
    mutationFn: (change: { name?: string; timeZone?: string }) => ds.company.update(tenant, change),
    onSuccess: (company: Company, change) => {
      if (change.name !== undefined) tracked('company.renamed')(company, change);
      set(company);
    },
  });
  return { setPackage, setSecurity, update, close };
}

/** One invitation, or several at once (row 48): one request each; the refusals come back, the rest are listed. */
function useInvites(tenant: string, refresh: () => void) {
  const ds = useDataSource();
  const invite = useMutation({
    mutationFn: (v: { email: string; role: Role }) => ds.company.invite(tenant, v.email, v.role),
    onSuccess: tracked('people.invite_sent', (_d, v) => ({ count: 1, role: v.role }), refresh),
  });
  const inviteMany = useMutation({
    mutationFn: (v: { emails: string[]; role: Role }) =>
      inviteEach(v.emails, (email) => ds.company.invite(tenant, email, v.role)),
    onSuccess: tracked('people.invite_sent', (refused, v) => ({
      count: v.emails.length - refused.length,
      role: v.role,
    })),
    onSettled: refresh,
  });
  return { invite, inviteMany };
}

/** People changes refresh the list. */
export function usePeopleChanges(tenant: string) {
  const ds = useDataSource();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: companyKeys.people(tenant) });
    void qc.invalidateQueries({ queryKey: [...companyKeys.company(tenant), 'changes'] });
  };
  const { invite, inviteMany } = useInvites(tenant, refresh);
  const setRole = useMutation({
    mutationFn: (v: { userId: string; role: Role }) => ds.company.setRole(tenant, v.userId, v.role),
    onSuccess: tracked('people.role_changed', (_d, v) => ({ role: v.role }), refresh),
  });
  const remove = useMutation({
    mutationFn: (person: Person) => ds.company.remove(tenant, person),
    onSuccess: tracked('people.removed', undefined, refresh),
  });
  const resend = useMutation({
    mutationFn: (email: string) => ds.company.resendInvite(tenant, email),
    onSuccess: refresh,
  });
  const signOut = useMutation({
    mutationFn: (userId: string) => ds.company.signOut(tenant, userId),
    onSuccess: tracked('people.signed_out_everywhere', undefined, refresh),
  });
  const resetSecondFactor = useMutation({
    mutationFn: (userId: string) => ds.company.resetSecondFactor(tenant, userId),
    onSuccess: tracked('people.second_factor_reset', undefined, refresh),
  });
  const setJobs = useMutation({
    mutationFn: (v: { userId: string; jobs: PersonJobs }) =>
      ds.company.setJobs(tenant, v.userId, v.jobs),
    onSuccess: tracked(
      'people.jobs_narrowed',
      (_d, v) => ({ all: v.jobs.all, count: v.jobs.jobIds.length }),
      refresh,
    ),
  });
  return { invite, inviteMany, setRole, remove, resend, signOut, resetSecondFactor, setJobs };
}
